
import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import {
    collection,
    query,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    orderBy,
    where,
    Timestamp,
    increment
} from 'firebase/firestore';

export interface StakingProduct {
    id: string;
    name: string;
    asset: string;
    apy: number; // Percentage
    minAmount: number;
    duration: number; // Days, 0 for flexible
    status: 'Active' | 'Inactive';
    description: string;
    totalStaked: number;
    participantCount: number;
    createdAt: Timestamp;
    updatedAt: Timestamp;
}

export interface UserStaking {
    id: string;
    userId: string;
    productId: string;
    productName: string;
    asset: string;
    amount: number;
    apy: number;
    stakedAt: Timestamp;
    status: 'Active' | 'Closed';
    closedAt?: Timestamp;
}

import { useTransactionManager } from '@/shared/hooks/useTransactionManager';

export function useStaking(userId?: string) {
    const [products, setProducts] = useState<StakingProduct[]>([]);
    const [myStakings, setMyStakings] = useState<UserStaking[]>([]);
    const [allStakings, setAllStakings] = useState<UserStaking[]>([]);
    const [loading, setLoading] = useState(true);
    const { addTransaction } = useTransactionManager(userId);

    // Subscribe to all active staking products
    useEffect(() => {
        const q = query(collection(db, 'staking_products'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(d => ({
                id: d.id,
                ...d.data()
            })) as StakingProduct[];
            setProducts(items);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Subscribe to specific user's staking participations
    useEffect(() => {
        if (!userId) return;
        const q = query(
            collection(db, 'user_stakings'),
            where('userId', '==', userId),
            orderBy('stakedAt', 'desc')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(d => ({
                id: d.id,
                ...d.data()
            })) as UserStaking[];
            setMyStakings(items);
        });
        return () => unsubscribe();
    }, [userId]);

    // Subscribe to all staking participations (Admin)
    useEffect(() => {
        const q = query(collection(db, 'user_stakings'), orderBy('stakedAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(d => ({
                id: d.id,
                ...d.data()
            })) as UserStaking[];
            setAllStakings(items);
        });
        return () => unsubscribe();
    }, []);

    // Admin Functions
    const addProduct = async (input: Partial<StakingProduct>) => {
        await addDoc(collection(db, 'staking_products'), {
            ...input,
            totalStaked: 0,
            participantCount: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    };

    const updateProduct = async (id: string, input: Partial<StakingProduct>) => {
        await updateDoc(doc(db, 'staking_products', id), {
            ...input,
            updatedAt: serverTimestamp()
        });
    };

    const deleteProduct = async (id: string) => {
        await deleteDoc(doc(db, 'staking_products', id));
    };

    // User Functions
    const joinStaking = async (product: StakingProduct, amount: number) => {
        if (!userId) throw new Error("User not authenticated");

        const stakingRef = await addDoc(collection(db, 'user_stakings'), {
            userId,
            productId: product.id,
            productName: product.name,
            asset: product.asset,
            amount,
            apy: product.apy,
            status: 'Active',
            stakedAt: serverTimestamp()
        });

        await addTransaction({
            userId,
            type: 'STAKING',
            asset: `${product.name} 참여`,
            amount: `-${amount} ${product.asset}`,
            status: 'COMPLETED'
        });

        await updateDoc(doc(db, 'staking_products', product.id), {
            totalStaked: increment(amount),
            participantCount: increment(1),
            updatedAt: serverTimestamp()
        });

        return stakingRef.id;
    };

    const cancelStaking = async (staking: UserStaking) => {
        if (staking.status !== 'Active') return;

        await updateDoc(doc(db, 'user_stakings', staking.id), {
            status: 'Closed',
            closedAt: serverTimestamp()
        });

        const userDocRef = doc(db, 'users', staking.userId);
        const refundAmount = staking.amount * 0.85; // 15% 수수료 공제

        await addTransaction({
            userId: staking.userId,
            type: 'STAKING',
            asset: `${staking.productName} 중도해지`,
            amount: `+${refundAmount} ${staking.asset}`,
            status: 'COMPLETED'
        });

        await updateDoc(userDocRef, {
            [staking.asset.toLowerCase()]: increment(refundAmount)
        });

        await updateDoc(doc(db, 'staking_products', staking.productId), {
            totalStaked: increment(-staking.amount),
            participantCount: increment(-1),
            updatedAt: serverTimestamp()
        });
    };

    return {
        products,
        myStakings,
        allStakings,
        loading,
        addProduct,
        updateProduct,
        deleteProduct,
        joinStaking,
        cancelStaking
    };
}
